import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Button, TextField, Checkbox, FormControlLabel, Chip, CircularProgress,
  Collapse, IconButton, Tooltip, Alert, LinearProgress,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import LockIcon from '@mui/icons-material/Lock';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { bloomberg } from '../theme/bloombergTheme';
import { TickerChip } from './TickerActionMenu';
import {
  unlockTranscripts, TRANSCRIPTS_PASSCODE_KEY,
  uploadTranscript, listTranscripts, getTranscript, reprocessTranscript, deleteTranscript, downloadTranscriptPdf,
  TranscriptRow, TranscriptStockMention,
} from '../services/api';

const CARD = { bgcolor: bloomberg.bg.secondary, border: `1px solid ${bloomberg.border.primary}`, borderRadius: 1, p: 2 };
const H = { fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: 0.5, color: bloomberg.text.muted, mb: 0.75 };
const BODY = { fontSize: '0.86rem', color: bloomberg.text.secondary, lineHeight: 1.65 };

const STANCE_COLOR: Record<string, string> = {
  bought: bloomberg.status.positive, adding: bloomberg.status.positive, bullish: bloomberg.status.positive,
  sold: bloomberg.status.negative, trimmed: bloomberg.status.negative, bearish: bloomberg.status.negative,
};

/**
 * Upload a meeting or call transcript (e.g. an Otter.ai .txt export) and get a
 * summary plus investment insights: who holds which view, every stock
 * mentioned (with speech-recognition errors resolved), price levels checked
 * against today's price, and action items.
 *
 * "Keep private" processes the transcript on this server with the local model
 * — nothing is sent to OpenAI, at the cost of speed and accuracy.
 */
/** Passcode gate: transcripts are private to Marc and Damien. The server checks
 *  the passcode on every request; this only asks for it once per session. */
export default function TranscriptsTab() {
  const [state, setState] = useState<'checking' | 'locked' | 'open'>('checking');
  const [code, setCode] = useState('');
  const [wrong, setWrong] = useState(false);

  useEffect(() => {
    let saved: string | null = null;
    try { saved = sessionStorage.getItem(TRANSCRIPTS_PASSCODE_KEY); } catch { /* storage blocked */ }
    if (!saved) { setState('locked'); return; }
    unlockTranscripts(saved).then((ok) => setState(ok ? 'open' : 'locked'));
  }, []);

  const submit = async () => {
    setWrong(false);
    if (await unlockTranscripts(code.trim())) setState('open');
    else setWrong(true);
  };

  if (state === 'open') return <TranscriptsContent />;
  if (state === 'checking') return <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress size={22} /></Box>;
  return (
    <Box sx={{ ...CARD, maxWidth: 380, mx: 'auto', mt: 4, textAlign: 'center' }}>
      <LockIcon sx={{ color: bloomberg.text.muted, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, mb: 0.5 }}>Transcripts are private</Typography>
      <Typography sx={{ ...BODY, mb: 2 }}>Enter the passcode to view meeting transcripts.</Typography>
      <Box component="form" onSubmit={(e: React.FormEvent) => { e.preventDefault(); submit(); }}
        sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
        <TextField size="small" type="password" value={code} autoFocus label="Passcode"
          onChange={(e) => { setCode(e.target.value); setWrong(false); }} error={wrong}
          helperText={wrong ? 'Incorrect passcode' : ' '} inputProps={{ inputMode: 'numeric', autoComplete: 'off' }} />
        <Button type="submit" variant="contained" disabled={!code.trim()} sx={{ height: 40 }}>Open</Button>
      </Box>
    </Box>
  );
}

function TranscriptsContent() {
  const [rows, setRows] = useState<TranscriptRow[]>([]);
  const [selected, setSelected] = useState<TranscriptRow | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState('');
  const [date, setDate] = useState('');
  const [text, setText] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const r = await listTranscripts();
      setRows(r);
      if (selected) {
        const cur = r.find((x) => x.id === selected.id);
        if (cur && cur.status !== selected.status) setSelected(await getTranscript(cur.id));
      }
    } catch { /* keep what we have */ }
  }, [selected]);

  // Load once on mount. (This project's ESLint has no react-hooks plugin, so
  // no disable comment: naming its rule is itself an error here.)
  useEffect(() => { refresh(); }, []);

  // Poll while anything is still being processed.
  useEffect(() => {
    if (!rows.some((r) => r.status === 'pending' || r.status === 'processing')) return;
    const id = window.setInterval(refresh, 10000);
    return () => window.clearInterval(id);
  }, [rows, refresh]);

  const onFile = (f: File | undefined) => {
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      setText(String(reader.result || ''));
      if (!title) setTitle(f.name.replace(/\.(txt|md|srt|vtt)$/i, '').replace(/[_-]+/g, ' '));
    };
    reader.readAsText(f);
  };

  const submit = async () => {
    setBusy(true); setError(null);
    try {
      const r = await uploadTranscript({ title: title || undefined, meeting_date: date || undefined, text, private: isPrivate });
      setTitle(''); setDate(''); setText(''); setIsPrivate(false); setShowForm(false);
      await refresh();
      setSelected(await getTranscript(r.id));
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Upload failed.');
    } finally {
      setBusy(false);
    }
  };

  const open = async (id: number) => setSelected(await getTranscript(id));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography sx={{ ...BODY, flex: 1, minWidth: 240 }}>
          Upload a meeting or call transcript (for example an Otter.ai .txt export) to get a summary, each person's
          views, every stock mentioned with its price levels checked against today, and the action items.
        </Typography>
        <Button variant="contained" size="small" startIcon={<UploadFileIcon />} onClick={() => setShowForm((v) => !v)}>
          {showForm ? 'Cancel' : 'New transcript'}
        </Button>
      </Box>

      <Collapse in={showForm}>
        <Box sx={{ ...CARD, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <TextField size="small" label="Title (optional)" value={title} onChange={(e) => setTitle(e.target.value)} sx={{ flex: '2 1 240px' }} />
            <TextField size="small" label="Meeting date" type="date" value={date} onChange={(e) => setDate(e.target.value)}
              InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 160px' }} />
            <Button component="label" size="small" variant="outlined" sx={{ flex: '0 0 auto' }}>
              Choose file (.txt)
              <input hidden type="file" accept=".txt,.md,.srt,.vtt,text/plain" onChange={(e) => onFile(e.target.files?.[0])} />
            </Button>
          </Box>
          <TextField multiline minRows={6} maxRows={16} size="small" placeholder="…or paste the transcript here"
            value={text} onChange={(e) => setText(e.target.value)} />
          <Typography sx={{ fontSize: '0.75rem', color: bloomberg.text.muted, mt: -1 }}>
            {text ? `${text.trim().split(/\s+/).length.toLocaleString()} words` : ''}
          </Typography>
          <FormControlLabel
            control={<Checkbox size="small" checked={isPrivate} onChange={(e) => setIsPrivate(e.target.checked)} />}
            label={
              <Typography sx={{ fontSize: '0.84rem', color: bloomberg.text.secondary }}>
                Keep private — process on this server only. Nothing is sent to OpenAI, but it takes several minutes and
                misheard company names are less likely to be resolved.
              </Typography>
            }
          />
          {error && <Alert severity="error">{error}</Alert>}
          <Box>
            <Button variant="contained" size="small" disabled={busy || text.trim().length < 200} onClick={submit}>
              {busy ? <CircularProgress size={16} /> : 'Upload and analyse'}
            </Button>
          </Box>
        </Box>
      </Collapse>

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexDirection: { xs: 'column', md: 'row' } }}>
        <Box sx={{ ...CARD, p: 0, width: { xs: '100%', md: 300 }, flexShrink: 0 }}>
          {rows.length === 0 && (
            <Typography sx={{ ...BODY, p: 2 }}>No transcripts yet.</Typography>
          )}
          {rows.map((r) => (
            <Box key={r.id} onClick={() => open(r.id)} sx={{
              px: 1.5, py: 1.1, cursor: 'pointer', borderBottom: `1px solid ${bloomberg.border.primary}`,
              bgcolor: selected?.id === r.id ? bloomberg.bg.tertiary : 'transparent',
              '&:hover': { bgcolor: bloomberg.bg.tertiary }, '&:last-child': { borderBottom: 'none' },
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                {r.private && <Tooltip title="Processed on this server only"><LockIcon sx={{ fontSize: 14, color: bloomberg.text.muted }} /></Tooltip>}
                <Typography noWrap sx={{ fontSize: '0.86rem', fontWeight: 600, color: bloomberg.text.primary, flex: 1 }}>
                  {r.title || `Transcript #${r.id}`}
                </Typography>
                <StatusChip status={r.status} />
              </Box>
              <Typography sx={{ fontSize: '0.74rem', color: bloomberg.text.muted }}>
                {r.meeting_date || (r.created_at || '').slice(0, 10)}
                {r.participants.length ? ` · ${r.participants.map((p) => p.name).join(', ')}` : ''}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
          {selected ? (
            <Detail t={selected} onChange={async (id) => { await refresh(); if (id) setSelected(await getTranscript(id)); else setSelected(null); }} />
          ) : (
            <Box sx={CARD}><Typography sx={BODY}>Select a transcript to see its summary and insights.</Typography></Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

function StatusChip({ status }: { status: TranscriptRow['status'] }) {
  if (status === 'completed') return null;
  const color = status === 'failed' ? bloomberg.status.negative : bloomberg.text.muted;
  return <Chip size="small" label={status === 'pending' || status === 'processing' ? 'analysing…' : status}
    sx={{ height: 18, fontSize: '0.66rem', color, borderColor: color }} variant="outlined" />;
}

function Detail({ t, onChange }: { t: TranscriptRow; onChange: (id: number | null) => void }) {
  const [showRaw, setShowRaw] = useState(false);
  const s = t.summary;
  const working = t.status === 'pending' || t.status === 'processing';

  const del = async () => {
    if (!window.confirm(`Delete "${t.title || 'this transcript'}"? This can't be undone.`)) return;
    await deleteTranscript(t.id);
    onChange(null);
  };
  const redo = async (priv?: boolean) => { await reprocessTranscript(t.id, priv); onChange(t.id); };
  const [exporting, setExporting] = useState(false);
  const exportPdf = async () => {
    setExporting(true);
    try { await downloadTranscriptPdf(t.id); } catch { window.alert('Could not create the PDF. Please try again.'); }
    setExporting(false);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={CARD}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, color: bloomberg.text.primary }}>{t.title || `Transcript #${t.id}`}</Typography>
            <Typography sx={{ fontSize: '0.76rem', color: bloomberg.text.muted }}>
              {t.meeting_date || (t.created_at || '').slice(0, 10)} · {(t.word_count || 0).toLocaleString()} words
              {t.processed_by ? ` · analysed by ${t.processed_by}` : ''}{t.private ? ' · private' : ''}
            </Typography>
          </Box>
          <Button size="small" variant="outlined" disabled={t.status !== 'completed' || exporting} onClick={exportPdf}
            startIcon={exporting ? <CircularProgress size={13} /> : <PictureAsPdfIcon sx={{ fontSize: 16 }} />}
            sx={{ textTransform: 'none', fontSize: '0.76rem', py: 0.25, mr: 0.5, whiteSpace: 'nowrap' }}>
            Export PDF
          </Button>
          <Tooltip title="Analyse again"><span><IconButton size="small" disabled={working} onClick={() => redo()}><RefreshIcon sx={{ fontSize: 18 }} /></IconButton></span></Tooltip>
          <Tooltip title="Delete"><IconButton size="small" onClick={del}><DeleteOutlineIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
        </Box>

        {working && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress />
            <Typography sx={{ ...BODY, mt: 1 }}>
              Analysing{t.private ? ' on this server — this takes several minutes' : ''}…
            </Typography>
          </Box>
        )}
        {t.status === 'failed' && (
          <Alert severity="error" sx={{ mt: 2 }} action={
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" onClick={() => redo()}>Retry</Button>
              {t.private && <Button size="small" onClick={() => redo(false)}>Retry with GPT-4o</Button>}
            </Box>
          }>
            {t.error_message || 'Analysis failed.'}
          </Alert>
        )}
        {s && (
          <Box sx={{ mt: 2 }}>
            <Typography sx={H}>Summary</Typography>
            <Typography sx={{ ...BODY, color: bloomberg.text.primary }}>{s.summary}</Typography>
          </Box>
        )}
      </Box>

      {s && (s.stock_mentions?.length ?? 0) > 0 && (
        <Box sx={CARD}>
          <Typography sx={H}>Stocks discussed</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {s.stock_mentions.map((m, i) => <StockRow key={i} m={m} />)}
          </Box>
          {t.private && s.stock_mentions.some((m) => !m.ticker) && (
            <Typography sx={{ fontSize: '0.74rem', color: bloomberg.text.muted, mt: 1, fontStyle: 'italic' }}>
              Processed privately: names are matched to tickers by exact name only, so misheard names are left unresolved.
            </Typography>
          )}
        </Box>
      )}

      {s && (s.participants?.length ?? 0) > 0 && (
        <Box sx={CARD}>
          <Typography sx={H}>Who thinks what</Typography>
          {s.participants.map((p, i) => (
            <Box key={i} sx={{ mb: 1 }}>
              <Typography sx={{ fontSize: '0.84rem', fontWeight: 700, color: bloomberg.text.primary }}>{p.name}</Typography>
              <Typography sx={BODY}>{p.views}</Typography>
            </Box>
          ))}
        </Box>
      )}

      {s && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
          {(s.action_items?.length ?? 0) > 0 && (
            <Box sx={CARD}>
              <Typography sx={H}>Action items</Typography>
              {s.action_items.map((a, i) => (
                <Typography key={i} sx={BODY}>☐ {a.task}{a.owner ? <Box component="span" sx={{ color: bloomberg.text.muted }}> — {a.owner}</Box> : ''}</Typography>
              ))}
            </Box>
          )}
          {(s.key_points?.length ?? 0) > 0 && (
            <Box sx={CARD}>
              <Typography sx={H}>Key points</Typography>
              {s.key_points.map((k, i) => <Typography key={i} sx={BODY}>· {k}</Typography>)}
            </Box>
          )}
          {(s.macro_views?.length ?? 0) > 0 && (
            <Box sx={CARD}>
              <Typography sx={H}>Macro views</Typography>
              {s.macro_views.map((v, i) => (
                <Typography key={i} sx={BODY}><b>{v.topic}</b>: {v.view}{v.speaker ? <Box component="span" sx={{ color: bloomberg.text.muted }}> — {v.speaker}</Box> : ''}</Typography>
              ))}
            </Box>
          )}
          {((s.decisions?.length ?? 0) > 0 || (s.open_questions?.length ?? 0) > 0) && (
            <Box sx={CARD}>
              {(s.decisions?.length ?? 0) > 0 && <>
                <Typography sx={H}>Decisions</Typography>
                {s.decisions.map((d, i) => <Typography key={i} sx={BODY}>· {d}</Typography>)}
              </>}
              {(s.open_questions?.length ?? 0) > 0 && <>
                <Typography sx={{ ...H, mt: 1.5 }}>Open questions</Typography>
                {s.open_questions.map((d, i) => <Typography key={i} sx={BODY}>· {d}</Typography>)}
              </>}
            </Box>
          )}
        </Box>
      )}

      {s && (s.themes?.length ?? 0) > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {s.themes.map((th, i) => <Chip key={i} label={th} size="small" variant="outlined" />)}
        </Box>
      )}

      <Box sx={CARD}>
        <Box onClick={() => setShowRaw((v) => !v)} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', color: bloomberg.text.muted }}>
          {showRaw ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
          <Typography sx={{ fontSize: '0.8rem' }}>{showRaw ? 'Hide' : 'Show'} the full transcript</Typography>
        </Box>
        <Collapse in={showRaw}>
          <Typography component="pre" sx={{ ...BODY, whiteSpace: 'pre-wrap', fontFamily: 'inherit', mt: 1, maxHeight: 480, overflow: 'auto' }}>
            {t.raw_text}
          </Typography>
        </Collapse>
      </Box>
    </Box>
  );
}

function StockRow({ m }: { m: TranscriptStockMention }) {
  const stanceColor = (m.stance && STANCE_COLOR[m.stance]) || bloomberg.text.muted;
  const money = (v: number) => v.toLocaleString(undefined, { maximumFractionDigits: 2 });
  return (
    <Box sx={{ py: 1, borderBottom: `1px solid ${bloomberg.border.primary}`, '&:last-child': { borderBottom: 'none' } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        {m.ticker && m.in_database ? <TickerChip symbol={m.ticker} /> : (
          <Chip size="small" label={m.ticker || 'unresolved'} variant="outlined"
            sx={{ fontFamily: 'monospace', color: m.ticker ? bloomberg.text.secondary : bloomberg.status.warning }} />
        )}
        <Typography sx={{ fontSize: '0.86rem', fontWeight: 600, color: bloomberg.text.primary }}>{m.company}</Typography>
        {m.heard_as && m.heard_as.toLowerCase() !== (m.company || '').toLowerCase() && (
          <Tooltip title="How the transcript spelt it — speech recognition errors are common">
            <Typography sx={{ fontSize: '0.74rem', color: bloomberg.text.muted, fontStyle: 'italic', cursor: 'help' }}>heard as “{m.heard_as}”</Typography>
          </Tooltip>
        )}
        {m.stance && (
          <Chip size="small" label={m.stance} variant="outlined" sx={{ height: 18, fontSize: '0.66rem', color: stanceColor, borderColor: stanceColor }} />
        )}
        {m.speaker && <Typography sx={{ fontSize: '0.76rem', color: bloomberg.text.muted }}>— {m.speaker}</Typography>}
        {m.confidence === 'low' && (
          <Tooltip title="Low confidence in which company was meant">
            <Typography sx={{ fontSize: '0.7rem', color: bloomberg.status.warning, cursor: 'help' }}>low confidence</Typography>
          </Tooltip>
        )}
        {m.price_now !== undefined && (
          <Typography sx={{ ml: 'auto', fontSize: '0.8rem', fontFamily: 'monospace', color: bloomberg.text.secondary }}>
            now {money(m.price_now)}
          </Typography>
        )}
      </Box>
      {m.view && <Typography sx={{ ...BODY, mt: 0.25 }}>{m.view}</Typography>}
      {(m.levels || []).map((l, i) => (
        <Typography key={i} sx={{ fontSize: '0.8rem', color: bloomberg.text.secondary, mt: 0.25 }}>
          <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{money(l.price)}</Box> — {l.meaning}
          {l.now !== undefined && l.distance_pct !== undefined && (
            <Box component="span" sx={{ color: l.position === 'below' ? bloomberg.status.negative : l.position === 'above' ? bloomberg.status.positive : bloomberg.text.muted }}>
              {' '}· today {money(l.now)}, {l.position === 'at' ? 'at this level' : `${Math.abs(l.distance_pct)}% ${l.position}`}
            </Box>
          )}
        </Typography>
      ))}
    </Box>
  );
}
